import { message } from 'antd';
import { useCallback, useMemo, useState } from 'react';

import { openRecordModal } from '@app/components/RecordModal/model/recordModalSlice';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { isValidTime } from '@app/shared/components/TimeInput';
import { useAppDispatch } from '@app/store';
import type { RecordInterface, UpdatedEmployeeInterface } from '@app/types';

import {
  getCalendarGridStepHeight,
  resolveNewRecordEndTime,
  snapTimeToStep,
} from '@pages/Calendar/utils';

import type { CalendarBreak, CalendarEvent } from '@pages/Calendar/data';
import {
  useCalendarRecordActions,
  useCalendarSlotMenu,
} from '@pages/Calendar/hooks';
import { useIndividualCalendarContext } from '@pages/IndividualCalendar/context';
import type { IndividualCalendarDayAccess } from '@pages/IndividualCalendar/data';
import {
  useIndividualCalendarData,
  useIndividualCalendarPermissions,
} from '@pages/IndividualCalendar/hooks';

import { useIndividualCalendarLayoutEffects } from './useIndividualCalendarLayoutEffects';

import type { Moment } from 'moment';
import type { RefCallback } from 'react';

const buildClientName = (event: CalendarEvent) =>
  [event.rawRecord.NameLast, event.rawRecord.NameFirst]
    .filter(Boolean)
    .join(' ') || null;

const getDayKey = (dayMoment: Moment) => dayMoment.format('YYYY-MM-DD');

interface SelectedBreak {
  // null — перерыв создаётся из меню шага сетки (task/11149).
  id: number | null;
  employeeId: number;
  employeeName: string;
  date: string;
  timeS: string;
  timeE: string;
}

export const useIndividualCalendarLayout = () => {
  const dispatch = useAppDispatch();
  const { showNotification } = useNotification();
  const {
    todayMoment,
    currentEmployeeId,
    visibleDays,
    intervalHeightPx,
    previewFields,
    gridStepMinutes,
    periodStartMoment,
    periodEndMoment,
    calendarDataRefreshKey,
    refreshCalendarData,
    goToPrevPeriodHandler,
    goToNextPeriodHandler,
    updateAvailableGridWidth,
  } = useIndividualCalendarContext();
  const [centerNode, setCenterNode] = useState<HTMLDivElement | null>(null);
  const {
    canShowContacts,
    getCanCreateRecordOnDate,
    getCanManageRecordOnDate,
    getCanOpenRecordOnDate,
    getCanViewDate,
  } = useIndividualCalendarPermissions({
    currentEmployeeId,
    todayMoment,
  });
  const {
    days,
    events,
    currentEmployeeMeta,
    isLoading,
    startTime: rawStartTime,
    endTime: rawEndTime,
    timeFormat,
    loadIndividualJournal,
  } = useIndividualCalendarData();
  const { checkRecordMove, applyRecordMove } = useCalendarRecordActions();
  const { slotMenu, openSlotMenuHandler, closeSlotMenuHandler } =
    useCalendarSlotMenu();
  const [selectedBreak, setSelectedBreak] = useState<SelectedBreak | null>(
    null
  );

  const centerRef = useCallback<RefCallback<HTMLDivElement>>((node) => {
    setCenterNode(node);
  }, []);

  const loadIndividualJournalHandler = useCallback(async () => {
    await loadIndividualJournal({
      currentEmployeeId,
      periodStartMoment,
      periodEndMoment,
      visibleDays,
    });
  }, [
    currentEmployeeId,
    loadIndividualJournal,
    periodEndMoment,
    periodStartMoment,
    visibleDays,
  ]);

  useIndividualCalendarLayoutEffects({
    calendarDataRefreshKey,
    centerNode,
    loadIndividualJournalHandler,
    updateAvailableGridWidth,
  });

  const visibleDayKeys = useMemo(
    () => visibleDays.map(getDayKey),
    [visibleDays]
  );

  const areDaysAlignedWithVisibleRange = useMemo(
    () =>
      days.length === visibleDayKeys.length &&
      days.every(({ date }, index) => date === visibleDayKeys[index]),
    [days, visibleDayKeys]
  );

  const isLayoutLoading = useMemo(
    () => isLoading || (days.length > 0 && !areDaysAlignedWithVisibleRange),
    [areDaysAlignedWithVisibleRange, days.length, isLoading]
  );

  const dayColumns = useMemo(() => days.map(({ column }) => column), [days]);
  const bookingStepMinutes = gridStepMinutes;

  const startTime = useMemo(
    () => snapTimeToStep(rawStartTime, bookingStepMinutes, 'floor'),
    [rawStartTime, bookingStepMinutes]
  );

  const endTime = useMemo(
    () => snapTimeToStep(rawEndTime, bookingStepMinutes, 'ceil'),
    [rawEndTime, bookingStepMinutes]
  );

  const gridStepHeightPx = useMemo(
    () =>
      getCalendarGridStepHeight({
        intervalHeightPx,
        stepMinutes: bookingStepMinutes,
      }),
    [bookingStepMinutes, intervalHeightPx]
  );

  const executorNonWorkingDates = useMemo(
    () =>
      days
        .filter(({ isExecutorNonWorkingDay }) => isExecutorNonWorkingDay)
        .map(({ date }) => date),
    [days]
  );

  const dayAccessByDate = useMemo(
    () =>
      days.reduce<Record<string, IndividualCalendarDayAccess>>(
        (acc, { date }) => {
          acc[date] = {
            canCreateRecords: getCanCreateRecordOnDate(date),
            canManageRecords: getCanManageRecordOnDate(date),
            canOpenRecords: getCanOpenRecordOnDate(date),
            isContentHidden: !getCanViewDate(date),
          };

          return acc;
        },
        {}
      ),
    [
      days,
      getCanCreateRecordOnDate,
      getCanManageRecordOnDate,
      getCanOpenRecordOnDate,
      getCanViewDate,
    ]
  );

  const modalEmployee = useMemo(
    () =>
      ({
        UserId: currentEmployeeId,
        Id: currentEmployeeId,
        NameFirst: currentEmployeeMeta?.nameFirst || '',
        NameLast: currentEmployeeMeta?.nameLast || '',
        NameMiddle: currentEmployeeMeta?.nameMiddle || '',
        NameFull: currentEmployeeMeta?.nameFull || '',
        Photo: currentEmployeeMeta?.photo || '',
        Position: currentEmployeeMeta?.position || '',
        PositionId: 0,
        WorkSchedules: [],
        WorkBreaks: [],
        Records: [],
      } as unknown as UpdatedEmployeeInterface),
    [currentEmployeeId, currentEmployeeMeta]
  );

  const slotClickHandler = useCallback(
    (date: string, time: string, freeEndTime: string) => {
      if (!getCanCreateRecordOnDate(date)) {
        showNotification('У вас нет прав на создание записи', 'frontWarning');

        return;
      }

      dispatch(
        openRecordModal({
          mode: 'create',
          employee: modalEmployee,
          time,
          endTime: resolveNewRecordEndTime({
            startTime: time,
            freeEndTime,
            gridStepMinutes: bookingStepMinutes,
          }),
          date,
        })
      );
    },
    [
      bookingStepMinutes,
      dispatch,
      getCanCreateRecordOnDate,
      modalEmployee,
      showNotification,
    ]
  );

  const onRecordClick = useCallback(
    (event: CalendarEvent) => {
      const recordDate = event.employeeId;

      if (!getCanOpenRecordOnDate(recordDate)) {
        showNotification('У вас нет прав на просмотр записи', 'frontWarning');

        return;
      }

      dispatch(
        openRecordModal({
          mode: 'edit',
          record: event.rawRecord as unknown as RecordInterface,
          employee: modalEmployee,
          clientId: event.rawRecord.ClientId || undefined,
          clientName: buildClientName(event),
          date: recordDate,
        })
      );
    },
    [dispatch, getCanOpenRecordOnDate, modalEmployee, showNotification]
  );

  const breakClickHandler = useCallback(
    (date: string, calendarBreak: CalendarBreak) => {
      const { timeStart: timeS, timeEnd: timeE } = calendarBreak;

      if (!isValidTime(timeS) || !isValidTime(timeE)) {
        return;
      }

      setSelectedBreak({
        id: calendarBreak.id,
        employeeId: currentEmployeeId,
        employeeName: currentEmployeeMeta?.nameFull || 'Исполнитель',
        date,
        timeS,
        timeE,
      });
    },
    [currentEmployeeId, currentEmployeeMeta]
  );

  // Пункт «Добавить перерыв» меню шага сетки: то же окно перерыва, что и в
  // журнале, время — у самого шага.
  const slotBreakClickHandler = useCallback(
    (date: string, time: string, endTime: string) => {
      if (!getCanCreateRecordOnDate(date)) {
        showNotification('У вас нет прав на создание записи', 'frontWarning');

        return;
      }

      if (!isValidTime(time) || !isValidTime(endTime)) {
        return;
      }

      setSelectedBreak({
        id: null,
        employeeId: currentEmployeeId,
        employeeName: currentEmployeeMeta?.nameFull || 'Исполнитель',
        date,
        timeS: time,
        timeE: endTime,
      });
    },
    [
      currentEmployeeId,
      currentEmployeeMeta,
      getCanCreateRecordOnDate,
      showNotification,
    ]
  );

  const closeBreakModalHandler = useCallback(() => {
    setSelectedBreak(null);
  }, []);

  const breakModalSuccessHandler = useCallback(() => {
    refreshCalendarData();
  }, [refreshCalendarData]);

  const onEventUpdate = useCallback(
    async (
      eventId: string,
      newStart: string,
      newEnd: string,
      newDate?: string
    ) => {
      const event = events.find(({ id }) => id === eventId);

      if (!event) {
        return;
      }

      const targetDate = newDate ?? event.employeeId;

      if (!getCanManageRecordOnDate(targetDate)) {
        message.warning('У вас нет прав на изменение записи');

        return;
      }

      const moveParams = {
        recordId: Number(eventId),
        newStart,
        newEnd,
        newEmployeeId: currentEmployeeId,
        date: targetDate,
      };
      const checkResult = await checkRecordMove(moveParams);

      if (!checkResult.canMove) {
        message.warning(checkResult.message ?? 'Перенос записи невозможен');

        return;
      }

      const isUpdated = await applyRecordMove(moveParams);

      if (isUpdated) {
        refreshCalendarData();
      }
    },
    [
      applyRecordMove,
      checkRecordMove,
      currentEmployeeId,
      events,
      getCanManageRecordOnDate,
      refreshCalendarData,
    ]
  );

  return {
    todayMoment,
    visibleDays,
    centerRef,
    days,
    executorNonWorkingDates,
    dayAccessByDate,
    dayColumns,
    isLoading: isLayoutLoading,
    intervalHeightPx,
    gridStepHeightPx,
    startTime,
    endTime,
    boundaryEndTime: rawEndTime,
    bookingStepMinutes,
    timeFormat,
    goToPrevPeriodHandler,
    goToNextPeriodHandler,
    selectedBreak,
    slotClickHandler,
    slotBreakClickHandler,
    breakClickHandler,
    closeBreakModalHandler,
    breakModalSuccessHandler,
    onEventUpdate,
    onRecordClick,
    canShowContacts,
    previewFields,
    slotMenu,
    openSlotMenuHandler,
    closeSlotMenuHandler,
  };
};
